import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { shareService } from '../../services/shareService';
import { setError, appendChannels, setLoading } from '../../store/slices/playlistSlice';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import logger from '../../utils/logger';
import { useFirestore } from '../../hooks/useFirestore';
import { usePlaylistAutoSave } from '../../hooks/usePlaylistAutoSave';

// Helper function to filter HTTPS channels (reused from PlaylistImport)
const filterHttpsChannels = (channels: any[]) => {
  const httpsChannels = channels.filter(channel => {
    try {
      const urlObj = new URL(channel.url);
      return urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  });
  return {
    httpsChannels,
    filteredCount: channels.length - httpsChannels.length
  };
};

export const AutoImport: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { savePlaylist } = useFirestore();
  const { autoSave } = usePlaylistAutoSave();
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  
  // Get playlist loading state and channels from Redux
  const { isLoading, channels } = useSelector((state: RootState) => state.playlist);
  const { user } = useSelector((state: RootState) => state.auth);
  
  logger.log('AutoImport - Component mounted with shareId:', shareId);

  useEffect(() => {
    const importPlaylist = async () => {
      if (!shareId) {
        navigate('/');
        return;
      }

      // Set loading state to true
      dispatch(setLoading(true));
      
      try {
        logger.log('AutoImport - Fetching shared playlist with ID:', shareId);
        
        // Fetch shared playlist
        const sharedChannels = await shareService.getSharedPlaylist(shareId);
        
        if (!sharedChannels) {
          dispatch(setError({ 
            message: t('errors.invalidShareLink', 'This share link has expired or is invalid'), 
            type: 'error' 
          }));
          navigate('/');
          return;
        }

        // Filter HTTPS channels
        const { httpsChannels, filteredCount } = filterHttpsChannels(sharedChannels);
        
        if (httpsChannels.length === 0) {
          dispatch(setError({ 
            message: t('errors.noHttpsChannels', 'No valid HTTPS channels found in the shared playlist'), 
            type: 'error' 
          }));
          navigate('/');
          return;
        }

        // Create a copy of existing channels to ensure we have the right data for saving
        const existingChannels = [...channels];
        logger.debug('Current channels in Redux state before update:', existingChannels.length);
        
        // Import channels
        dispatch(appendChannels(httpsChannels));
        logger.debug('Dispatched appendChannels action, channels to add:', httpsChannels.length);
        
        // Auto-save to Firestore if user is authenticated
        if (user?.uid) {
          setIsAutoSaving(true);
          try {
            logger.debug('Starting auto-save process for shared playlist import');
            
            // Wait for state to update - ensure state propagation
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Calculate what the new state should be (for logging)
            const expectedChannelCount = existingChannels.length + httpsChannels.length;
            logger.debug('Expected channel count after update:', expectedChannelCount);
            logger.debug('Actual channel count in Redux before save:', channels.length);
            
            // Save all channels including the newly imported ones
            if (channels.length < expectedChannelCount) {
              logger.debug('Redux state not yet updated, using direct save with combined channels');
              const combinedChannels = [...existingChannels, ...httpsChannels];
              await savePlaylist(user.uid, combinedChannels);
            } else {
              logger.debug('Using autoSave with updated Redux state');
              await autoSave();
            }
            
            logger.debug('Auto-save completed successfully for shared playlist import');
            
            // Show success message with save confirmation
            const message = filteredCount > 0 
              ? t('playlist.importSharedSuccessWithFiltered', 'Successfully imported and saved {{count}} HTTPS channels from shared playlist ({{filtered}} non-HTTPS channels were filtered out)', { count: httpsChannels.length, filtered: filteredCount })
              : t('playlist.importSharedSuccess', 'Successfully imported and saved {{count}} HTTPS channels from shared playlist', { count: httpsChannels.length });
            
            dispatch(setError({ message, type: 'success' }));
          } catch (error) {
            logger.error('Failed to auto-save shared playlist:', error);
            dispatch(setError({ 
              message: t('errors.autoSaveFailed', 'Channels imported but failed to save automatically. Please try saving manually.'),
              type: 'error' 
            }));
          } finally {
            setIsAutoSaving(false);
          }
        } else {
          // Show regular success message without save confirmation
          const message = filteredCount > 0 
            ? t('playlist.importSharedSuccessWithFiltered', 'Successfully imported {{count}} HTTPS channels from shared playlist ({{filtered}} non-HTTPS channels were filtered out)', { count: httpsChannels.length, filtered: filteredCount })
            : t('playlist.importSharedSuccess', 'Successfully imported {{count}} HTTPS channels from shared playlist', { count: httpsChannels.length });
          
          dispatch(setError({ message, type: 'success' }));
        }

        // Navigate to main view
        navigate('/');
      } catch (error) {
        logger.error('Error importing shared playlist:', error);
        dispatch(setError({ 
          message: t('errors.sharedPlaylistImportError', 'Failed to import shared playlist'), 
          type: 'error' 
        }));
        navigate('/');
      } finally {
        // Set loading state back to false
        dispatch(setLoading(false));
      }
    };

    importPlaylist();
  }, [shareId, dispatch, navigate, t, channels, user, savePlaylist, autoSave]);

  // Show loading indicator
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh]">
      <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        {t('playlist.importing', 'Importing Playlist...')}
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        {t('playlist.importingWait', 'Please wait while we import your playlist')}
      </p>
      {isAutoSaving && (
        <p className="text-sm text-blue-500 dark:text-blue-400 mt-2">
          {t('playlist.autoSaving', 'Saving changes...')}
        </p>
      )}
    </div>
  );
}; 